import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

const REFRESH_THRESHOLD = 76;
const MAX_PULL_DISTANCE = 112;

function pageIsAtTop() {
  return window.scrollY <= 0
    && document.documentElement.scrollTop <= 0
    && document.body.scrollTop <= 0;
}

function shouldIgnoreTarget(target: EventTarget | null) {
  if (!(target instanceof Element)) return false;
  return Boolean(target.closest(
    'input, textarea, select, [contenteditable="true"], .drawer, .dialog, .modal, [role="dialog"]',
  ));
}

export function PullToRefresh() {
  const [distance, setDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const tracking = useRef(false);
  const pulling = useRef(false);
  const distanceRef = useRef(0);

  useEffect(() => {
    const reset = () => {
      tracking.current = false;
      pulling.current = false;
      distanceRef.current = 0;
      setDistance(0);
    };

    const onTouchStart = (event: TouchEvent) => {
      if (refreshing || event.touches.length !== 1 || !pageIsAtTop() || shouldIgnoreTarget(event.target)) {
        reset();
        return;
      }
      const touch = event.touches[0];
      startX.current = touch.clientX;
      startY.current = touch.clientY;
      tracking.current = true;
      pulling.current = false;
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!tracking.current || refreshing || event.touches.length !== 1) return;
      const touch = event.touches[0];
      const deltaX = touch.clientX - startX.current;
      const deltaY = touch.clientY - startY.current;

      if (!pulling.current) {
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          reset();
          return;
        }
        if (deltaY <= 5) return;
        pulling.current = true;
      }

      if (deltaY <= 0 || !pageIsAtTop()) {
        reset();
        return;
      }

      event.preventDefault();
      const dampedDistance = Math.min(MAX_PULL_DISTANCE, Math.round(deltaY * 0.48));
      distanceRef.current = dampedDistance;
      setDistance(dampedDistance);
    };

    const onTouchEnd = () => {
      if (!tracking.current || refreshing) return;
      const shouldRefresh = pulling.current && distanceRef.current >= REFRESH_THRESHOLD;
      tracking.current = false;
      pulling.current = false;

      if (!shouldRefresh) {
        distanceRef.current = 0;
        setDistance(0);
        return;
      }

      distanceRef.current = REFRESH_THRESHOLD;
      setDistance(REFRESH_THRESHOLD);
      setRefreshing(true);
      if ('vibrate' in navigator) navigator.vibrate(18);
      window.setTimeout(() => window.location.reload(), 320);
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', reset, { passive: true });

    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', reset);
    };
  }, [refreshing]);

  const progress = Math.min(1, distance / REFRESH_THRESHOLD);
  const visible = refreshing || distance > 0;

  return (
    <div
      className={`pull-refresh ${visible ? 'visible' : ''} ${refreshing ? 'refreshing' : ''}`}
      style={{
        '--pull-distance': `${distance}px`,
        '--pull-rotation': `${Math.round(progress * 250)}deg`,
      } as CSSProperties}
      aria-live="polite"
      aria-hidden={!visible}
    >
      <span className="pull-refresh-ring">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M20 6v5h-5" />
          <path d="M19 11a7.5 7.5 0 1 0 .2 4.3" />
        </svg>
      </span>
      <span className="pull-refresh-label">
        {refreshing ? 'Refreshing…' : progress >= 1 ? 'Release to refresh' : 'Pull to refresh'}
      </span>
    </div>
  );
}
